import type { Tone } from "@fox1ck-org/ui";

import { useCampaignCount } from "@/lib/services/campaign-service";
import { attentionCount, useDataHealth } from "@/lib/services/data-health-service";
import { useListLanders } from "@/lib/services/lander-service";
import { useListOffers } from "@/lib/services/offer-service";
import { OfferStatus } from "@gen/tracker/v1/offer_pb";
import { useListTrackingDomains } from "@/lib/services/tracking-domain-service";
import type { RailId } from "@/shell/places";

export interface RailCount {
  value: number;
  tone?: Exclude<Tone, "primary">;
}

/** A total is a slow-moving fact: the rail does not need it fresher than a minute. */
const STALE_MS = 60_000;

/**
 * The rail's counts (доска Rail): how many campaigns, offers, landers and
 * domains the library holds — each list RPC's own `total`, one row fetched —
 * and the data-health issues worth a look, as a warn badge. The numbers are
 * the server's; the browser counts nothing. Archived things are not counted:
 * ListLanders leaves them out by default, ListOffers is asked for every live
 * status (an empty status filter there means «every status», archive too).
 */
const LIVE_OFFER = [OfferStatus.ACTIVE, OfferStatus.PAUSED, OfferStatus.CAPPED];
export function useRailCounts(): Partial<Record<RailId, RailCount>> {
  const campaigns = useCampaignCount();
  const offers = useListOffers({ limit: 1, statuses: LIVE_OFFER }, { staleTime: STALE_MS });
  const landers = useListLanders({ limit: 1 }, { staleTime: STALE_MS });
  const domains = useListTrackingDomains({ limit: 1 });
  const health = useDataHealth();
  const issues = attentionCount(health.data);
  return {
    campaigns: campaigns.data === undefined ? undefined : { value: campaigns.data },
    offers: offers.data === undefined ? undefined : { value: offers.data.total },
    landers: landers.data === undefined ? undefined : { value: landers.data.total },
    domains: domains.data === undefined ? undefined : { value: domains.data.total },
    health: issues > 0 ? { value: issues, tone: "warn" } : undefined,
  };
}
